import React, { useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import { Box, ToggleButton, ToggleButtonGroup, Popover, IconButton, Divider } from '@mui/material';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import FormatColorFillIcon from '@mui/icons-material/FormatColorFill';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight';
import FormatAlignJustifyIcon from '@mui/icons-material/FormatAlignJustify';
import Underline from '@tiptap/extension-underline';
import TextStyle from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import TextAlign from '@tiptap/extension-text-align';

const COLORS = [
  '#000000', // Black
  '#FF0000', // Red
  '#00FF00', // Green
  '#0000FF', // Blue
  '#FFA500', // Orange
  '#800080', // Purple
  '#008080', // Teal
  '#FFC0CB', // Pink
];

interface RichTextEditorProps {
  content: string;
  onChange: (content: string) => void;
  placeholder?: string;
}

function RichTextEditor({ content, onChange, placeholder }: RichTextEditorProps): React.JSX.Element {
  const [colorAnchorEl, setColorAnchorEl] = useState<HTMLElement | null>(null);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      TextStyle,
      Color,
      Link.configure({
        openOnClick: false,
      }),
    ],
    content,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
  });

  if (!editor) {
    return <></>;
  }

  const handleColorClick = (event: React.MouseEvent<HTMLElement>): void => {
    setColorAnchorEl(event.currentTarget);
  };

  const handleColorClose = (): void => {
    setColorAnchorEl(null);
  };

  const handleColorSelect = (color: string): void => {
    editor.chain().focus().setColor(color).run();
    handleColorClose();
  };

  const handleAlignment = (alignment: 'left' | 'center' | 'right' | 'justify'): void => {
    editor.chain().focus().setTextAlign(alignment).run();
  };

  const currentColor = editor.getAttributes('textStyle').color || '#000000';
  const currentAlignment = editor.getAttributes('paragraph').textAlign || 'left';

  return (
    <Box sx={{
      border: '1px solid #e0e0e0',
      borderRadius: 1,
      overflow: 'hidden',
      '& .ProseMirror': {
        minHeight: '150px',
        padding: 2,
        outline: 'none',
        '& p': {
          margin: 0,
        },
      }
    }}>
      <Box sx={{
        borderBottom: '1px solid #e0e0e0',
        p: 0.5,
        bgcolor: '#f5f5f5',
        display: 'flex',
        alignItems: 'center',
        gap: 1
      }}>
        <ToggleButtonGroup size="small">
          <ToggleButton
            value="bold"
            selected={editor.isActive('bold')}
            onClick={() => editor.chain().focus().toggleBold().run()}
          >
            <FormatBoldIcon />
          </ToggleButton>
          <ToggleButton
            value="italic"
            selected={editor.isActive('italic')}
            onClick={() => editor.chain().focus().toggleItalic().run()}
          >
            <FormatItalicIcon />
          </ToggleButton>
          <ToggleButton
            value="underline"
            selected={editor.isActive('underline')}
            onClick={() => editor.chain().focus().toggleUnderline().run()}
          >
            <FormatUnderlinedIcon />
          </ToggleButton>
        </ToggleButtonGroup>

        <Divider orientation="vertical" flexItem />

        <ToggleButtonGroup size="small" value={currentAlignment}>
          <ToggleButton
            value="left"
            onClick={() => handleAlignment('left')}
          >
            <FormatAlignLeftIcon />
          </ToggleButton>
          <ToggleButton
            value="center"
            onClick={() => handleAlignment('center')}
          >
            <FormatAlignCenterIcon />
          </ToggleButton>
          <ToggleButton
            value="right"
            onClick={() => handleAlignment('right')}
          >
            <FormatAlignRightIcon />
          </ToggleButton>
          <ToggleButton
            value="justify"
            onClick={() => handleAlignment('justify')}
          >
            <FormatAlignJustifyIcon />
          </ToggleButton>
        </ToggleButtonGroup>

        <Divider orientation="vertical" flexItem />

        <IconButton
          size="small"
          onClick={handleColorClick}
          sx={{
            color: currentColor,
            '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)' }
          }}
        >
          <FormatColorFillIcon />
        </IconButton>
        <Popover
          open={Boolean(colorAnchorEl)}
          anchorEl={colorAnchorEl}
          onClose={handleColorClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
        >
          <Box sx={{ p: 1, display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1 }}>
            {COLORS.map((color) => (
              <Box
                key={color}
                onClick={() => handleColorSelect(color)}
                sx={{
                  width: 24,
                  height: 24,
                  bgcolor: color,
                  border: '1px solid #e0e0e0',
                  borderRadius: 1,
                  cursor: 'pointer',
                  '&:hover': {
                    transform: 'scale(1.1)',
                    transition: 'transform 0.2s',
                  },
                }}
              />
            ))}
          </Box>
        </Popover>
      </Box>
      <Box sx={{
        direction: 'rtl',
        textAlign: 'right',
      }}>
        <EditorContent editor={editor} />
      </Box>
    </Box>
  );
}

export default RichTextEditor; 